import React from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { Icon } from '@iconify/react';

const AlertBox = ({
  severity = 'info',
  title,
  children,
  onClose,
  icon,
  variant = 'filled', // 'filled' or 'outlined'
  sx = {},
  ...props
}) => {
  const theme = useTheme();

  const severityConfig = {
    info: {
      icon: icon || <InfoIcon />,
      bgColor: {
        filled: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : '#e3f2fd',
        outlined: 'transparent'
      },
      borderColor: theme.palette.mode === 'dark' ? '#2196f3' : '#2196f3',
      textColor: theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0',
      iconColor: theme.palette.mode === 'dark' ? '#90caf9' : '#2196f3'
    },
    success: {
      icon: icon || <CheckCircleIcon />,
      bgColor: {
        filled: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : '#e8f5e9',
        outlined: 'transparent'
      },
      borderColor: theme.palette.mode === 'dark' ? '#4caf50' : '#4caf50',
      textColor: theme.palette.mode === 'dark' ? '#a5d6a7' : '#2e7d32',
      iconColor: theme.palette.mode === 'dark' ? '#a5d6a7' : '#4caf50'
    },
    warning: {
      icon: icon || <WarningIcon />,
      bgColor: {
        filled: theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.15)' : '#fff3e0',
        outlined: 'transparent'
      },
      borderColor: theme.palette.mode === 'dark' ? '#ffa726' : '#ff9800',
      textColor: theme.palette.mode === 'dark' ? '#ffd54f' : '#ef6c00',
      iconColor: theme.palette.mode === 'dark' ? '#ffd54f' : '#ff9800'
    },
    error: {
      icon: icon || <ErrorIcon />,
      bgColor: {
        filled: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.15)' : '#ffebee',
        outlined: 'transparent'
      },
      borderColor: theme.palette.mode === 'dark' ? '#f44336' : '#f44336',
      textColor: theme.palette.mode === 'dark' ? '#ef9a9a' : '#c62828',
      iconColor: theme.palette.mode === 'dark' ? '#ef9a9a' : '#f44336'
    },
    custom: {
      icon: icon || <InfoIcon />,
      bgColor: {
        filled: theme.palette.mode === 'dark' ? 'rgba(251,188,4,0.15)' : '#fff3cd',
        outlined: 'transparent'
      },
      borderColor: theme.palette.mode === 'dark' ? '#b38f00' : '#ffc107',
      textColor: theme.palette.mode === 'dark' ? '#ffd54f' : '#856404',
      iconColor: theme.palette.mode === 'dark' ? '#ffd54f' : '#ffc107'
    }
  };

  const config = severityConfig[severity] || severityConfig.info;

  return (
    <Box
      sx={{
        backgroundColor: config.bgColor[variant],
        border: `1px solid ${config.borderColor}`,
        borderRadius: 1,
        padding: 3,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        position: 'relative',
        ...sx
      }}
      {...props}
    >
      <Box
        sx={{
          color: config.iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: 0.5
        }}
      >
        {config.icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {title && (
          <Typography
            variant="subtitle1"
            sx={{
              color: config.textColor,
              fontWeight: 600,
              mb: children ? 0.5 : 0
            }}
          >
            {title}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{
            color: config.textColor,
            opacity: 0.9
          }}
        >
          {children}
        </Typography>
      </Box>

      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: config.iconColor,
            position: 'absolute',
            top: 8,
            right: 8,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default AlertBox;

// Compact version for inline use
export const InlineAlert = ({ severity = 'info', children, sx = {} }) => {
  const theme = useTheme();
  
  const severityColors = {
    info: {
      bg: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.1)' : '#e3f2fd',
      color: theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'
    },
    success: {
      bg: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : '#e8f5e9',
      color: theme.palette.mode === 'dark' ? '#a5d6a7' : '#2e7d32'
    },
    warning: {
      bg: theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.1)' : '#fff3e0',
      color: theme.palette.mode === 'dark' ? '#ffd54f' : '#ef6c00'
    },
    error: {
      bg: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : '#ffebee',
      color: theme.palette.mode === 'dark' ? '#ef9a9a' : '#c62828'
    }
  };

  const colors = severityColors[severity] || severityColors.info;

  return (
    <Box
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        borderRadius: 0.5,
        padding: '4px 8px',
        fontSize: '0.75rem',
        fontWeight: 500,
        display: 'inline-block',
        ...sx
      }}
    >
      {children}
    </Box>
  );
};